import { all, debounce, put, select } from "redux-saga/effects";

import {
  AuctionPenAction,
  BidderRegistrationAction,
  BillingDocumentAction,
  BillingRunAction,
  BusinessAction,
  CheckpointAction,
  fetchGeoData,
  LedgerEntryAction,
  ManualAdjustmentAction,
  NominationAction,
  PaymentAction,
  requestAttachmentsChanges,
  requestConsignmentsChanges,
  requestPropertiesChanges,
  requestScansChanges,
  SaleAction,
  SaleLotAction,
  SavedViewAction,
  ServerHeartbeatAction,
  WeighLotAction,
  WeighLotScanAction,
} from "actions";

import { IntegrationCredentialAction } from "actions/integrationCredentials";
import { InterestSettingsAction } from "actions/interest";
import { LedgerAccountAction } from "actions/ledgerAccounts";
import { MasterLedgerAccountAction } from "actions/masterLedgerAccounts";
import { PenScanLotAction } from "actions/penScanLots";
import { ReceivalLotAction } from "actions/receivalLots";
import { VendorCommissionBandAction } from "actions/vendorCommissionBands";

import { GET_LIVESTOCK_SALE_DATA_CHANGES } from "constants/actionTypes";

import {
  actionFilters,
  ruleActionTypes,
  inSalePermissionCheckActionTypes,
} from "lib/actionFilters";

import {
  getActiveBillingRunId,
  getCurrentSale,
  getIsFetchingAuctionPens,
  getIsFetchingBidders,
  getIsFetchingBillingDocuments,
  getIsFetchingBillingRuns,
  getIsFetchingBusinesses,
  getIsFetchingCheckpoints,
  getIsFetchingConsignments,
  getIsFetchingInterestSettings,
  getIsFetchingLedgerAccounts,
  getIsFetchingLedgerEntries,
  getIsFetchingManualAdjustments,
  getIsFetchingMasterLedgerAccounts,
  getIsFetchingNominations,
  getIsFetchingPayments,
  getIsFetchingPenScanLots,
  getIsFetchingProperties,
  getIsFetchingReceivalLots,
  getIsFetchingSaleLots,
  getIsFetchingSales,
  getIsFetchingSavedViews,
  getIsFetchingVendorCommissionBands,
  getIsFetchingWeighLots,
  getIsFetchingWeighLotScans,
  getIsReadOnlyByBillingRunId,
} from "selectors";

function* fetchLivestockSaleData(action) {
  const state = yield select();

  yield put(fetchGeoData());
  yield put(ServerHeartbeatAction.request());

  yield all(
    inSalePermissionCheckActionTypes
      .map(action => actionFilters[action.dataType](state, true))
      .filter(Boolean)
      .map(action => put(action)),
  );

  if (!getIsFetchingConsignments(state)) {
    yield put(requestConsignmentsChanges(action.sale));
  }

  if (!getIsFetchingSales(state)) {
    yield put(
      SaleAction.requestChanges({
        changesSince: state.sales.lastModifiedTimestamp,
        dontChangeSale: true,
      }),
    );
  }

  if (!getIsFetchingSaleLots(state)) {
    yield put(
      SaleLotAction.requestChanges({
        changesSince: state.saleLots.lastModifiedTimestamp,
      }),
    );
  }
  if (!state.scanners.isFetching) {
    yield put(requestScansChanges(action.sale));
  }
  if (!getIsFetchingBusinesses(state)) {
    yield put(
      BusinessAction.requestChanges({
        changesSince: state.businessesV2.lastModifiedTimestamp,
      }),
    );
  }
  if (!getIsFetchingProperties(state)) {
    yield put(requestPropertiesChanges());
  }

  if (!getIsFetchingAuctionPens(state)) {
    yield put(
      AuctionPenAction.requestChanges({
        changesSince: state.auctionPens.lastModifiedTimestamp,
      }),
    );
  }

  if (!getIsFetchingManualAdjustments(state)) {
    yield put(
      ManualAdjustmentAction.requestChanges({
        changesSince: state.manualAdjustments.lastModifiedTimestamp,
      }),
    );
  }

  if (!getIsFetchingPayments(state)) {
    yield put(
      PaymentAction.requestChanges({
        changesSince: state.payments.lastModifiedTimestamp,
        livestockSalesIdIn: [action.sale.livestocksale_id],
      }),
    );
  }

  if (!getIsFetchingNominations(state)) {
    yield put(
      NominationAction.requestChanges({
        changesSince: state.nominations.lastModifiedTimestamp,
      }),
    );
  }

  if (!getIsFetchingCheckpoints(state)) {
    yield put(
      CheckpointAction.requestChanges({
        changesSince: state.checkpoints.lastModifiedTimestamp,
      }),
    );
  }

  if (!getIsFetchingSavedViews(state)) {
    yield put(
      SavedViewAction.requestChanges({
        changesSince: state.savedViews.lastModifiedTimestamp,
      }),
    );
  }

  if (!getIsFetchingVendorCommissionBands(state)) {
    yield put(
      VendorCommissionBandAction.requestChanges({
        changesSince: state.vendorCommissionBands.lastModifiedTimestamp,
      }),
    );
  }

  if (!getIsFetchingInterestSettings(state)) {
    yield put(
      InterestSettingsAction.requestChanges({
        changesSince: state.interestSettings.lastModifiedTimestamp,
      }),
    );
  }

  if (!getIsFetchingReceivalLots(state)) {
    yield put(
      ReceivalLotAction.requestChanges({
        changesSince: state.receivalLots.lastModifiedTimestamp,
      }),
    );
  }

  if (!getIsFetchingPenScanLots(state)) {
    yield put(
      PenScanLotAction.requestChanges({
        changesSince: state.penScanLots.lastModifiedTimestamp,
      }),
    );
  }

  if (!getIsFetchingWeighLots(state)) {
    yield put(
      WeighLotAction.requestChanges({
        changesSince: state.weighLots.lastModifiedTimestamp,
      }),
    );
  }

  if (!getIsFetchingWeighLotScans(state)) {
    yield put(
      WeighLotScanAction.requestChanges({
        changesSince: state.weighLotScans.lastModifiedTimestamp,
      }),
    );
  }

  if (
    getCurrentSale(state)?.using_registered_bidders &&
    !getIsFetchingBidders(state)
  ) {
    yield put(
      BidderRegistrationAction.requestChanges({
        changesSince: state.bidderRegistrations.lastModifiedTimestamp,
      }),
    );
  }

  // No isFetching, no changes since.
  yield put(requestAttachmentsChanges(action.sale));

  // Billing
  const activeBillingRunId = getActiveBillingRunId(state);
  if (activeBillingRunId) {
    // If we have a writeable billing run active, check it for changes.

    const readOnly = getIsReadOnlyByBillingRunId(activeBillingRunId)(state);
    if (readOnly === false) {
      yield put(BillingRunAction.checkForChanges(activeBillingRunId));
    }

    // Get all the billing rules
    yield all(
      ruleActionTypes
        .map(actionSet => actionFilters[actionSet.dataType](state, true))
        .filter(Boolean)
        .map(action => put(action)),
    );

    // Standard get changes since.
    if (!getIsFetchingBillingRuns(state)) {
      yield put(
        BillingRunAction.requestChanges({
          changesSince: state.billingRuns.lastModifiedTimestamp,
          livestockSalesIdIn: action.sale.livestocksale_id,
        }),
      );
    }
    if (!getIsFetchingBillingDocuments(state)) {
      yield put(
        BillingDocumentAction.requestChanges({
          changesSince: state.billingDocuments.lastModifiedTimestamp,
          billingRunId: activeBillingRunId,
        }),
      );
    }
    if (!getIsFetchingLedgerEntries(state)) {
      yield put(
        LedgerEntryAction.requestChanges({
          changesSince: state.ledgerEntries.lastModifiedTimestamp,
          documentBillingRunId: activeBillingRunId,
        }),
      );
    }
  }

  yield put(
    IntegrationCredentialAction.requestChanges({
      changesSince: state.integrationCredentials.changesSince,
    }),
  );
  if (!getIsFetchingLedgerAccounts(state)) {
    yield put(
      LedgerAccountAction.requestChanges({
        changesSince: state.ledgerAccounts.changesSince,
      }),
    );
  }
  if (!getIsFetchingMasterLedgerAccounts(state)) {
    yield put(
      MasterLedgerAccountAction.requestChanges({
        changesSince: state.masterLedgerAccounts.changesSince,
      }),
    );
  }
}

export default function* livestockSaleDataSaga() {
  // There is a hard to trace possibility of over-calling this - likely based on a re-re-re-re render around
  // updateStore.  This results in heartbeat firing a few dozen times.
  // It's pretty unlikely we'd want to call this multiple times a second, so, don't.
  yield debounce(1000, GET_LIVESTOCK_SALE_DATA_CHANGES, fetchLivestockSaleData);
}
